'use client'

import { AnalyticsPixels } from '@/components/analytics/analytics-pixels'
import { DeferredInsiderScript } from '@/components/analytics/deferred-insider-script'
import { GTMPageView } from '@/components/analytics/gtm-page-view'
import { InsiderClient } from '@/components/analytics/insider-client'
import { LazyGTM } from '@/components/analytics/lazy-gtm'
import { useGTMConsent } from '@/hooks/analytics/use-gtm-consent'
import { useInsiderConsent } from '@/hooks/analytics/use-insider-consent'
import { usePixelConsent } from '@/hooks/analytics/use-pixel-consent'

// Env flags — evaluated at build time (NEXT_PUBLIC_* baked into the bundle)
const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID
const META_PIXEL_ID = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID
const TIKTOK_PIXEL_ID = process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID
const FLAG_GTM = process.env.NEXT_PUBLIC_TRACKING_GTM_ENABLED !== 'false'
const FLAG_INSIDER = process.env.NEXT_PUBLIC_TRACKING_INSIDER_ENABLED !== 'false'
const FLAG_META = process.env.NEXT_PUBLIC_TRACKING_FACEBOOK_ENABLED !== 'false'
const FLAG_TIKTOK = process.env.NEXT_PUBLIC_TRACKING_TIKTOK_ENABLED !== 'false'

/**
 * All third-party trackers gated behind c15t consent.
 * Must be mounted inside <ConsentManagerProvider> (i.e. inside <AppProviders>).
 *
 * Consent categories:
 *   - `measurement` → GTM, GTM page-view events, Insider
 *   - `marketing`   → Meta Pixel, TikTok Pixel
 */
export function ConsentGatedTrackers() {
  const { enabled: gtmConsented } = useGTMConsent()
  const { enabled: pixelConsented } = usePixelConsent()
  const { enabled: insiderConsented } = useInsiderConsent()

  return (
    <>
      {/* ── Google Tag Manager (measurement) ───────────────────────── */}
      {FLAG_GTM && GTM_ID && gtmConsented && <LazyGTM gtmId={GTM_ID} />}

      {/* ── GTM page-view events on route change (measurement) ─────── */}
      {FLAG_GTM && gtmConsented && <GTMPageView />}

      {/* ── Meta Pixel + TikTok Pixel (marketing) ──────────────────── */}
      <AnalyticsPixels
        metaPixelId={FLAG_META && pixelConsented ? META_PIXEL_ID : undefined}
        tiktokPixelId={FLAG_TIKTOK && pixelConsented ? TIKTOK_PIXEL_ID : undefined}
      />

      {/* ── Insider CDP: script + basket sync + tracker (measurement) ─ */}
      {FLAG_INSIDER && insiderConsented && (
        <>
          <DeferredInsiderScript />
          <InsiderClient />
        </>
      )}
    </>
  )
}
